import { useState } from 'react';
import { DragDropProvider } from '@dnd-kit/react';
import { isSortable } from '@dnd-kit/react/sortable';
import { Modal } from '../Modal';
import { ModalFooter } from '../ModalFooter';
import { FormGroup } from '../FormGroup';
import { SpinnerInput } from '../SpinnerInput';
import { DateTimeFormatHelp } from '../DateTimeFormatHelp';
import { useCreateReport, type ReportField } from '../../hooks/useReports';
import { defaultResourceReportFields } from './resourceReportFields';
import { ReportFieldRow } from './ReportFieldRow';
import type { BulkContext } from '../AddTagControl';

const DEFAULT_DATETIME = "yyyy-MM-dd'T'HH:mm:ss.SSSZ";

function charPreview(code: number): string {
  return Number.isFinite(code) && code >= 0 && code <= 255 ? String.fromCharCode(code) : '';
}

/**
 * Resource Report — React port of dialog_report.html / AddReportModalCtrl. Reports on
 * the current view (collections + tag filter), not the row selection. The Customize
 * Fields panel reorders (drag), renames (outputName), and toggles the ~78 default fields.
 */
export function ResourceReportModal({ bulk, onClose }: { bulk: BulkContext; onClose: () => void }) {
  const createReport = useCreateReport(bulk.projectId);

  const [name, setName] = useState(
    `${bulk.search ? 'Search' : 'Resource'} Report ${new Date().toLocaleString()}`
  );
  const [multiValueCharCode, setMultiValueCharCode] = useState(59);
  const [dateTimeFormat, setDateTimeFormat] = useState(DEFAULT_DATETIME);
  const [hasHeaderRow, setHasHeaderRow] = useState(true);
  const [fields, setFields] = useState<ReportField[]>(defaultResourceReportFields);
  const [customize, setCustomize] = useState(false);
  const [showDateHelp, setShowDateHelp] = useState(false);
  const [fieldSearch, setFieldSearch] = useState('');

  const valid = name.trim().length > 0 && name.length <= 255 && dateTimeFormat.trim().length > 0;

  const setField = (idx: number, updated: ReportField) =>
    setFields((fs) => fs.map((f, i) => (i === idx ? updated : f)));
  const setAll = (isEnabled: boolean) => setFields((fs) => fs.map((f) => ({ ...f, isEnabled })));
  const matches = (f: ReportField) =>
    !fieldSearch || f.name.toLowerCase().includes(fieldSearch.toLowerCase());

  const submit = () => {
    if (!valid) return;
    createReport.mutate(
      {
        projectId: bulk.projectId,
        type: bulk.search ? 'resource_report_search' : 'resource_report',
        format: 'csv',
        name,
        multiValueCharCode,
        dateTimeFormat,
        hasHeaderRow,
        fields,
        collectionIds: bulk.collectionIds,
        filter: bulk.filter,
        columnFilters: bulk.columnFilters ?? null,
        // add_report_modal.js:153-157 — note the inverted-sounding names: `isRawSearch`
        // is true for an ADVANCED search, and an advanced expression travels
        // JSON-stringified in `query`. There is no `searchExpression` on this report.
        ...(bulk.search
          ? {
              isRawSearch: bulk.search.advanced,
              query: bulk.search.advanced ? JSON.stringify(bulk.search.expression) : bulk.search.query,
            }
          : {}),
      },
      { onSuccess: onClose }
    );
  };

  return (
    <Modal
      title="Generate Report"
      icon="fas fa-file-alt"
      large
      onClose={onClose}
      onSubmit={(e) => { e.preventDefault(); submit(); }}
      formClassName="form-horizontal"
      footer={
        <ModalFooter onClose={onClose} nextLabel="Save" nextDisabled={!valid}
          saving={createReport.isPending} savingLabel="Saving…" />
      }
    >
      <FormGroup label="Project" labelCols="col-sm-4 text-right" inputCols="col-sm-8">
        <p className="form-control-static"><i className="fas fa-gavel" /> {bulk.projectName ?? ''}</p>
      </FormGroup>

      <FormGroup label="Report name" htmlFor="rr-name" labelCols="col-sm-4 text-right" inputCols="col-sm-8">
        <input id="rr-name" type="text" className="form-control" value={name} maxLength={255}
          placeholder="Report Name" disabled={createReport.isPending} onChange={(e) => setName(e.target.value)} />
      </FormGroup>

      <FormGroup label="Multi Value" htmlFor="rr-mv" labelCols="col-sm-4 text-right" inputCols="col-sm-8">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <SpinnerInput id="rr-mv" value={multiValueCharCode} onChange={setMultiValueCharCode}
            min={0} max={255} required disabled={createReport.isPending} style={{ width: 80 }} />
          <input type="text" className="form-control uneditable-input" style={{ width: 50 }}
            value={charPreview(multiValueCharCode)} readOnly disabled />
        </div>
      </FormGroup>

      <FormGroup
        label={
          <>
            Date Time Format{' '}
            <button type="button" className="btn btn-default btn-xs" onClick={() => setShowDateHelp((v) => !v)}>
              <i className="fas fa-question-circle" title="Show definitions" />
            </button>
          </>
        }
        htmlFor="rr-dtf"
        labelCols="col-sm-4 text-right"
        inputCols="col-sm-8"
      >
        <input id="rr-dtf" type="text" className="form-control" value={dateTimeFormat} maxLength={255}
          disabled={createReport.isPending} onChange={(e) => setDateTimeFormat(e.target.value)} />
      </FormGroup>

      {showDateHelp && <DateTimeFormatHelp />}

      <div className="form-group">
        <div className="col-sm-8 col-sm-offset-4">
          <button type="button" className={`btn btn-default${customize ? ' active' : ''}`}
            onClick={() => setCustomize((v) => !v)}>
            Customize Fields
          </button>
        </div>
      </div>

      {customize && (
        <div>
          <legend>Custom Fields</legend>

          <FormGroup label="Include header row" labelCols="col-sm-4 text-right" inputCols="col-sm-8">
            <div className="checkbox" style={{ marginTop: 0 }}>
              <input id="rr-header" type="checkbox" checked={hasHeaderRow}
                onChange={(e) => setHasHeaderRow(e.target.checked)} />
              <label htmlFor="rr-header" />
            </div>
          </FormGroup>

          <div className="form-group">
            <div className="col-sm-12">
              <div className="input-group">
                <div className="input-group-btn">
                  <button type="button" className="btn btn-default" onClick={() => setAll(true)}>All</button>
                  <button type="button" className="btn btn-default" onClick={() => setAll(false)}>None</button>
                </div>
                <input type="text" className="form-control" placeholder="Filter Fields"
                  value={fieldSearch} onChange={(e) => setFieldSearch(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="row" style={{ fontWeight: 600 }}>
            <div className="col-sm-6" style={{ paddingLeft: 40 }}>Field</div>
            <div className="col-sm-6" style={{ paddingLeft: 40 }}>Output Name</div>
          </div>

          <DragDropProvider
            onDragEnd={({ canceled, operation }) => {
              if (canceled || !isSortable(operation.source)) return;
              const { initialIndex, index } = operation.source.sortable;
              if (initialIndex === index) return;
              setFields((fs) => {
                const next = [...fs];
                const [moved] = next.splice(initialIndex, 1);
                next.splice(index, 0, moved);
                return next;
              });
            }}
          >
            <ul className="format-fields list-unstyled">
              {fields.map((field, i) => (
                <ReportFieldRow key={field.name} field={field} index={i} hidden={!matches(field)}
                  onChange={(u) => setField(i, u)} />
              ))}
            </ul>
          </DragDropProvider>
        </div>
      )}
    </Modal>
  );
}
